import React from 'react';
import { cn } from './model/derive';
import { HANDOFF_TITLE, SOURCE_LABEL } from './model/taskList';
import { stripScheme } from './model/handoffSlots';
import { ReactComponent as CheckIcon } from '@repo/common/assets/icons/16px/icon_16px_check.svg';
import { ReactComponent as CrossIcon } from '@repo/common/assets/icons/16px/icon_16px_cancel.svg';

/** Task List 두 그룹(기본 항목 잠금 · 과제 항목, 번호 이어서) · 검수할 링크 슬롯 — 상세 · 수락 · 범위 변경에서 같이 쓴다 */

/** ✓ · ✗ 표시 — 글자 대신 아이콘 (Safari 에서 글자 기호가 깨져 보였다) */
const MarkIcon = ({ kind }) =>
  kind === 'fail' ? (
    <CrossIcon aria-hidden className='h-3.5 w-3.5' />
  ) : (
    <CheckIcon aria-hidden className='h-3.5 w-3.5' />
  );

const cardClass = 'rounded-2xl border border-green-deep03/[0.06] bg-grayscale-gray00 shadow-4';

const Marks = ({ result, onPick }) => (
  <span className='flex shrink-0 gap-1.5'>
    {[
      ['pass', 'Pass', 'bg-functional-success01 text-functional-success04 shadow-[inset_0_0_0_1px_#7eeac9]'],
      ['fail', 'Fail', 'bg-functional-failed01 text-functional-failed04 shadow-[inset_0_0_0_1px_#ff7474]'],
    ].map(([key, text, on]) => (
      <button
        key={key}
        type='button'
        aria-pressed={result === key}
        onClick={() => onPick(result === key ? null : key)}
        className={cn(
          'flex h-8 items-center gap-1 rounded-full px-3 text-[13px] font-medium',
          result === key
            ? on
            : 'bg-grayscale-gray00 text-text-secondary shadow-[inset_0_0_0_1px_#d5d2cd] hover:text-text-primary',
        )}
      >
        <MarkIcon kind={key} />
        {text}
      </button>
    ))}
  </span>
);

const Row = ({ n, tag, mode, result, onPick, children }) => (
  <li className='flex min-h-[52px] flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t border-green-deep03/[0.08] px-4 py-2 first:border-t-0'>
    <span className='flex min-w-0 flex-1 items-start gap-2 text-[14px] leading-[150%]'>
      {n != null && (
        <span className='w-5 shrink-0 text-right tabular-nums text-text-secondary'>{n}.</span>
      )}
      <span className='min-w-0 text-text-primary'>{children}</span>
      {tag && (
        <span className='inline-flex h-6 shrink-0 items-center rounded-full bg-grayscale-gray01 px-2.5 text-[12px] font-medium text-text-tertiary shadow-[inset_0_0_0_1px_#d5d2cd]'>
          {tag}
        </span>
      )}
    </span>
    {mode === 'review' ? (
      <Marks result={result} onPick={onPick} />
    ) : mode === 'sender' ? (
      <span className='flex shrink-0 items-center gap-1 text-[13px] text-functional-success04'>
        <CheckIcon aria-hidden className='h-3.5 w-3.5' />
        Checked by sender
      </span>
    ) : mode === 'ro' && result ? (
      <span
        className={cn(
          'flex shrink-0 items-center gap-1 text-[13px] font-medium',
          result === 'fail' ? 'text-functional-failed04' : 'text-functional-success04',
        )}
      >
        <MarkIcon kind={result} />
        {result === 'fail' ? 'Fail' : 'Pass'}
      </span>
    ) : null}
  </li>
);

const GroupTitle = ({ children }) => (
  <span className='text-[13px] leading-[140%] text-text-secondary'>{children}</span>
);

/**
 * @param results { 'link:{label}' | 'item:{label}': 'pass' | 'fail' }
 * @param onPick  (key, value) => void — review 일 때
 */
const TaskListGroups = ({
  type,
  base,
  items,
  sources,
  mode = 'plain',
  results = {},
  onPick,
}) => {
  const pick = (key) => (v) => onPick?.(key, v);
  return (
    <div className='flex flex-col gap-3'>
      {base.length > 0 && (
        <div className='flex flex-col gap-1.5'>
          <GroupTitle>Base items · {HANDOFF_TITLE[type]}</GroupTitle>
          <ul className={cardClass}>
            {base.map((label, i) => (
              <Row
                key={label}
                n={i + 1}
                mode={mode}
                result={results[`item:${label}`]}
                onPick={pick(`item:${label}`)}
              >
                {label}
              </Row>
            ))}
          </ul>
        </div>
      )}
      {items.length > 0 && (
        <div className='flex flex-col gap-1.5'>
          <GroupTitle>Task items</GroupTitle>
          <ul className={cardClass}>
            {items.map((label, i) => (
              <Row
                key={label}
                n={base.length + i + 1}
                tag={sources ? SOURCE_LABEL[sources[i]] : null}
                mode={mode}
                result={results[`item:${label}`]}
                onPick={pick(`item:${label}`)}
              >
                {label}
              </Row>
            ))}
          </ul>
        </div>
      )}
      {base.length === 0 && items.length === 0 && (
        <span className='text-[14px] text-text-tertiary'>No Task List items</span>
      )}
    </div>
  );
};

export const LinkSlotList = ({ links, mode = 'plain', results = {}, onPick }) =>
  links?.length > 0 ? (
    <div aria-label='Link slots' className='flex flex-col gap-1.5'>
      <GroupTitle>Link slots</GroupTitle>
      <ul className={cardClass}>
        {links.map((l) => (
          <Row
            key={l.label}
            mode={mode}
            result={results[`link:${l.label}`]}
            onPick={(v) => onPick?.(`link:${l.label}`, v)}
          >
            <span className='flex flex-col gap-0.5'>
              <span>{l.label}</span>
              <a
                href={l.url}
                target='_blank'
                rel='noreferrer'
                className='break-all text-[12px] leading-[140%] text-green-deep03'
              >
                {stripScheme(l.url)}
              </a>
            </span>
          </Row>
        ))}
      </ul>
    </div>
  ) : null;

export default TaskListGroups;
